import { inject } from '@angular/core';
import { Router, CanActivateFn, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { KeycloakService } from 'keycloak-angular';
import { RolesApiService, Role, ROLE_PRIORITY } from '@angular-workspace/services';
import { ToasterService } from '../services/toaster.service';

/**
 * Helper function to check for stored Keycloak data in localStorage/sessionStorage
 * This helps detect if user was logged in before page refresh
 */
function checkStoredKeycloakData(): boolean {
  try {
    // Check for Keycloak tokens in various storage locations
    const keycloakStorageKeys = [
      'keycloak-token',
      'kc-access',
      'kc-refresh',
      'KEYCLOAK_TOKEN',
      'KEYCLOAK_REFRESH_TOKEN',
    ];
    
    // Check localStorage
    for (const key of keycloakStorageKeys) {
      if (localStorage.getItem(key)) {
        return true;
      }
    }
    
    // Check sessionStorage
    for (const key of keycloakStorageKeys) {
      if (sessionStorage.getItem(key)) {
        return true;
      }
    }
    
    // Check for Keycloak's internal storage patterns
    const allStorageKeys = [
      ...Object.keys(localStorage),
      ...Object.keys(sessionStorage)
    ];
    
    // Look for any Keycloak-related keys
    return allStorageKeys.some(key => 
      key.toLowerCase().includes('keycloak') || 
      key.toLowerCase().includes('kc-')
    );
  } catch (error) {
    return false;
  }
}

/**
 * Auth Guard for role-based access control
 * Protects routes based on user roles and login status
 */
export const authGuard: CanActivateFn = async (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  // Inject all services at the top level (required for functional guards)
  const keycloak = inject(KeycloakService);
  const router = inject(Router);
  const rolesService = inject(RolesApiService);
  const toaster = inject(ToasterService);

  // Role-restricted panels (require both login AND a specific role)
  const protectedPanels = ['/consumer', '/provider', '/org-admin', '/admin'];
  const currentPath = state.url;
  const isPanelRoute = protectedPanels.some(panel => currentPath.startsWith(panel));

  // Debounce repeated "Access denied" toasters (guard can run multiple times per navigation)
  const ACCESS_DENIED_DEBOUNCE_MS = 3000;
  const lastAccessDeniedKey = 'auth-guard-last-access-denied';
  const lastAccessDenied = parseInt(sessionStorage.getItem(lastAccessDeniedKey) || '0', 10);

  // For non-panel routes (e.g. /contact-us, /contact-us/thank-you) we only need
  // to confirm the user is authenticated — no role check needed.
  // Use a direct Keycloak check here instead of the complex fallback logic below
  // (which is lenient about stale storage tokens and would let unauthenticated users through).
  if (!isPanelRoute) {
    try {
      const loggedIn = await keycloak.isLoggedIn();
      if (!loggedIn) {
        keycloak.login({ redirectUri: window.location.origin + state.url });
        return false;
      }
      return true;
    } catch {
      keycloak.login({ redirectUri: window.location.origin + state.url });
      return false;
    }
  }

  try {
    
    // Wait for Keycloak to be initialized (important for page refresh)
    // Try to get the instance - if it fails, wait a bit and try again
    let keycloakInstance;
    let attempts = 0;
    const maxAttempts = 10; // Increased attempts for page refresh
    
    while (attempts < maxAttempts) {
      try {
        keycloakInstance = keycloak.getKeycloakInstance();
        if (keycloakInstance) {
          break;
        }
      } catch (e) {
        // Instance not ready yet
      }
      
      if (attempts < maxAttempts - 1) {
        await new Promise(resolve => setTimeout(resolve, 200)); // Wait 200ms
      }
      attempts++;
    }
    
    // Check if user is logged in - be more patient on page refresh
    let isLoggedIn = false;
    
    // First, check if we have tokens stored (indicates user was logged in)
    const hasStoredTokens = keycloakInstance && 
      (keycloakInstance.token || keycloakInstance.refreshToken);
    
    // Also check localStorage/sessionStorage for Keycloak tokens as fallback
    const hasStoredKeycloakData = checkStoredKeycloakData();
    
    try {
      // On page refresh, Keycloak might need more time to restore session
      // Give it more time - up to 5 seconds
      const loginCheckPromise = keycloak.isLoggedIn();
      const timeoutPromise = new Promise<boolean>((resolve) => {
        setTimeout(() => {
          // If we have stored tokens, don't give up yet - user is likely logged in
          if (hasStoredTokens || hasStoredKeycloakData) {
            resolve(true); // Assume logged in if we have tokens
          } else {
            resolve(false);
          }
        }, 5000); // Increased to 5 seconds for refresh scenarios
      });
      
      isLoggedIn = await Promise.race([loginCheckPromise, timeoutPromise]);
      
      // If still not logged in but we have tokens, wait a bit more and check again
      if (!isLoggedIn && (hasStoredTokens || hasStoredKeycloakData)) {
        console.log('Tokens found but isLoggedIn() returned false, waiting for Keycloak to restore session...');
        // Wait a bit more for Keycloak to restore session
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Try one more time
        try {
          isLoggedIn = await keycloak.isLoggedIn();
        } catch (e) {
          // If still fails but we have tokens, assume logged in
          if (hasStoredTokens || hasStoredKeycloakData) {
            isLoggedIn = true;
          }
        }
      }
      
      // Final check: if we have tokens but isLoggedIn is false, trust the tokens
      if (!isLoggedIn && (hasStoredTokens || hasStoredKeycloakData)) {
        console.log('Tokens found, assuming user is logged in (Keycloak may still be initializing)');
        isLoggedIn = true;
      }
    } catch (loginCheckError) {
      console.error('Error checking login status:', loginCheckError);
      
      // On error, check if we have tokens as a fallback
      if (hasStoredTokens || hasStoredKeycloakData) {
        console.log('Error checking login but tokens found, assuming logged in');
        isLoggedIn = true;
      }
    }

    // Only redirect if we're absolutely sure the user is logged out
    // (no tokens anywhere, and Keycloak confirms not logged in)
    if (!isLoggedIn && !hasStoredTokens && !hasStoredKeycloakData) {
      console.log('User not logged in (no tokens found), redirecting to landing page');
      router.navigate(['/']);
      return false;
    }
    
    // If we have tokens but isLoggedIn is false, allow access (Keycloak is still initializing)
    if (!isLoggedIn && (hasStoredTokens || hasStoredKeycloakData)) {
      console.log('User has tokens, allowing access while Keycloak initializes');
      return true;
    }
    
    // User is logged in - preserve the current route (don't redirect)
    console.log('User is logged in, allowing access to:', currentPath);

    // User is logged in - check roles (with error handling)
    try {
      // Try to get roles with timeout
      const rolesPromise = rolesService.getRoles();
      const rolesTimeout = new Promise<void>((resolve) => {
        setTimeout(() => resolve(), 3000); // 3 second timeout
      });
      
      await Promise.race([rolesPromise, rolesTimeout]);
      
      const userRoles = rolesService.getUserRoles();

      // If no roles fetched, allow access temporarily for development
      if (!userRoles || userRoles.length === 0) {
        console.warn('No roles fetched - allowing access for development');
        return true;
      }

      // Determine which panel the user is trying to access
      let requiredRole: Role | null = null;
      
      if (currentPath.startsWith('/admin')) {
        requiredRole = Role.ADMIN;
      } else if (currentPath.startsWith('/org-admin')) {
        requiredRole = Role.ORG_ADMIN;
      } else if (currentPath.startsWith('/provider')) {
        requiredRole = Role.PROVIDER;
      } else if (currentPath.startsWith('/consumer')) {
        requiredRole = Role.CONSUMER;
      }

      // Check if user has the required role (use hasRole to handle aliases like cos_admin -> admin)
      if (requiredRole) {
        const hasRequiredRole = rolesService.hasRole(requiredRole);

        if (!hasRequiredRole) {
          // User doesn't have access to this panel
          if (Date.now() - lastAccessDenied > ACCESS_DENIED_DEBOUNCE_MS) {
            sessionStorage.setItem(lastAccessDeniedKey, String(Date.now()));
            toaster.warning(
              `Access denied. You don't have permission to access this panel.`,
              4000
            );
          }

          // Redirect to their highest role panel
          const userPanelRoute = rolesService.getUserPanelRoute();
          router.navigate([userPanelRoute]);
          return false;
        }
      }

      // User has access
      return true;
    } catch (error) {
      console.error('Error checking roles:', error);
      // On error, allow access during development
      console.warn('Allowing access due to role check error (development mode)');
      return true; // Temporarily allow access
    }
  } catch (error) {
    console.error('Auth guard critical error:', error);
    // On critical error, redirect to landing page for protected routes
    router.navigate(['/']);
    return false;
  }
};

/**
 * Logout Guard - Handles logout redirects
 * Use this on routes that should trigger logout message
 */
export const logoutGuard: CanActivateFn = async (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const keycloak = inject(KeycloakService);
  const router = inject(Router);
  const toaster = inject(ToasterService);

  try {
    const isLoggedIn = await keycloak.isLoggedIn();

    if (!isLoggedIn) {
      // Mark this as a logout redirect
      sessionStorage.setItem('logout-redirect', 'true');
      toaster.info("You've successfully logged out", 3000);
      router.navigate(['/']);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Logout guard error:', error);
    return true;
  }
};
